/*
 * @Author: Zhouqi
 * @Date: 2025-10-29 17:19:50
 * @LastEditors: Zhouqi
 * @LastEditTime: 2025-12-17 11:54:01
 */
import dynamic from 'next/dynamic';
import React, { memo, useCallback, useLayoutEffect } from 'react';

import DelayedRender from '../DelayRender';
import DisplayHeader from './display-header';
import MenuSkeleton from './left-menu/MenuSkeleton';
// import InviteFriends from './inviteFriends';
import LogoIcon from './logo';
import styles from './style.module.scss';

interface indexProps {
  children: React.ReactNode;
}

const SIDEBAR_EXPANDED_WIDTH = 222;
const SIDEBAR_COLLAPSED_WIDTH = 100;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'fc.layout.sidebar.collapsed';
const SIDEBAR_COLLAPSED_CHANGE_EVENT = 'fc.layout.sidebar.collapsed.change';

const LeftMenu = dynamic(() => import('./left-menu'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <MenuSkeleton />
    </DelayedRender>
  ),
});

const Layout: React.FC<indexProps> = memo((props) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = React.useState(56);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(true);
  const sidebarWidth = isSidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (stored == null) return;
    setIsSidebarCollapsed(stored === '1');
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
        window.dispatchEvent(
          new CustomEvent(SIDEBAR_COLLAPSED_CHANGE_EVENT, {
            detail: { collapsed: next },
          }),
        );
      }
      return next;
    });
  }, []);

  return (
    <div className="w-full h-full overflow-hidden">
      <div className="flex-1 w-full h-full min-w-[1200px] bg-no-repeat bg-center bg-app overflow-hidden text-app-primary">
        <div
          className="flex w-full h-full overflow-hidden transition-[padding] duration-200 ease-out"
          style={{ paddingLeft: sidebarWidth }}
        >
          <div className={styles['left-menu']} style={{ width: sidebarWidth }}>
            <LogoIcon collapsed={isSidebarCollapsed} sidebarWidth={sidebarWidth} />
            <LeftMenu
              collapsed={isSidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
            />
            {/* <InviteFriends /> */}
          </div>
          <div className="flex-1 overflow-hidden">
            <DisplayHeader ref={headerRef} />
            <div
              className="overflow-hidden"
              style={{
                height: `calc(100vh - ${headerHeight}px)`,
              }}
            >
              {props.children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

Layout.displayName = 'Layout';

export default Layout;
